/**
 * maintenanceGuard.ts
 *
 * Decides where a navigation should land given the maintenance state.
 *
 * Kept separate from `router/index.ts` and free of store and router
 * imports, so the rule - which is the part that is easy to get subtly
 * wrong - can be tested as a pure function.
 */

/** The route that shows the maintenance notice. */
export const MAINTENANCE_PATH = '/maintenance'

/** Where a signed-out user goes; reachable even during maintenance. */
const LOGIN_PATH = '/login'

/** Where a user is sent once maintenance is over. */
const HOME_PATH = '/'

/** Inputs to the maintenance redirect decision. */
export interface MaintenanceRedirectParams {
  /** The path being navigated to. */
  path: string
  /** Whether remote configuration currently reports maintenance. */
  isMaintenanceMode: boolean
  /** Whether the signed-in user may keep working during maintenance. */
  isAdmin: boolean
}

/**
 * Returns the path to redirect to, or null to allow the navigation.
 *
 * Sign-in stays reachable during maintenance on purpose: a user is only
 * recognised as an admin after logging in, so blocking `/login` would put
 * the admin bypass behind the page it exists to skip.
 *
 * @param params - The target path and the current maintenance state.
 * @returns The redirect target, or null when the navigation may proceed.
 */
export function resolveMaintenanceRedirect(
  params: MaintenanceRedirectParams,
): string | null {
  const { path, isMaintenanceMode, isAdmin } = params

  if (!isMaintenanceMode) {
    return path === MAINTENANCE_PATH ? HOME_PATH : null
  }

  if (isAdmin || path === MAINTENANCE_PATH || path === LOGIN_PATH) {
    return null
  }

  return MAINTENANCE_PATH
}
